/**
 * Rekap Kehadiran — Modul Kehadiran Tahap 7 (2026-10-14).
 *
 * Satu sumber data terpadu via GET /attendance/rekap dan /attendance/dashboard.
 * Scope ditegakkan server (BOD/HR = semua; atasan non-BOD = user yang
 * penugasannya ber-reviewer = dia). Panel karyawan biasa: tombol disembunyikan
 * (403 dari server). Snapshot — BUKAN pelacakan real-time.
 *
 * Sub-tab: Dashboard | Tabel Rekap (dgn filter & export) | Timeline Detail.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  BarChart3, Download, Filter, RefreshCw, AlertTriangle, MapPin, Users,
  Clock, LogIn, LogOut, ShieldAlert, Coffee, Timer, UserX, CalendarCheck,
} from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SearchableSelect from "@/components/SearchableSelect";

const BUCKET_META = {
  di_lokasi: { label: "Di Lokasi", icon: MapPin, iconCls: "text-emerald-600", activeCls: "border-emerald-500 bg-emerald-50" },
  tugas_luar: { label: "Tugas Luar", icon: Users, iconCls: "text-sky-600", activeCls: "border-sky-500 bg-sky-50" },
  izin_pribadi: { label: "Izin Pribadi", icon: Coffee, iconCls: "text-amber-600", activeCls: "border-amber-500 bg-amber-50" },
  terlambat: { label: "Terlambat", icon: Clock, iconCls: "text-rose-600", activeCls: "border-rose-500 bg-rose-50" },
  belum_masuk: { label: "Belum Masuk", icon: UserX, iconCls: "text-slate-600", activeCls: "border-slate-500 bg-slate-50" },
  belum_pulang: { label: "Belum Pulang", icon: LogOut, iconCls: "text-amber-600", activeCls: "border-amber-500 bg-amber-50" },
  cuti_izin: { label: "Cuti / Izin", icon: CalendarCheck, iconCls: "text-indigo-600", activeCls: "border-indigo-500 bg-indigo-50" },
  lembur_aktif: { label: "Lembur Aktif", icon: Timer, iconCls: "text-orange-600", activeCls: "border-orange-500 bg-orange-50" },
  pengajuan_menunggu: { label: "Pengajuan Menunggu", icon: ShieldAlert, iconCls: "text-amber-600", activeCls: "border-amber-500 bg-amber-50" },
  perlu_koreksi: { label: "Perlu Koreksi", icon: AlertTriangle, iconCls: "text-rose-600", activeCls: "border-rose-500 bg-rose-50" },
};
const STATUS_META = {
  hadir: { label: "Hadir", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  incomplete: { label: "Belum Pulang", cls: "text-amber-700 bg-amber-50 border-amber-200" },
  needs_review: { label: "Perlu Peninjauan", cls: "text-rose-700 bg-rose-50 border-rose-200" },
  tidak_hadir: { label: "Tidak Hadir", cls: "text-rose-700 bg-rose-50 border-rose-200" },
  cuti: { label: "Cuti / Izin", cls: "text-indigo-700 bg-indigo-50 border-indigo-200" },
  libur: { label: "Libur", cls: "text-slate-500 bg-slate-50 border-slate-200" },
  needs_schedule: { label: "Belum Ada Jadwal", cls: "text-slate-400 bg-slate-50 border-slate-200" },
};

function fmtTime(iso, tz = "Asia/Jakarta") {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", timeZone: tz }); }
  catch { return iso; }
}
function fmtHM(min) {
  if (!min) return "0m";
  const h = Math.floor(min / 60), m = min % 60;
  return h ? `${h}j ${m}m` : `${m}m`;
}
function todayStr() { return new Date().toISOString().slice(0, 10); }
function monthStartStr() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; }

/* ================= Dashboard ================= */
function DashboardSection({ onDrill }) {
  const [data, setData] = useState(null);
  const [date, setDate] = useState(todayStr());
  const [open, setOpen] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(() => {
    setErr(null);
    api.get(`/attendance/dashboard?date=${date}`).then((r) => setData(r.data))
      .catch((e) => { setErr(formatDetail(e.response?.data?.detail) || "Gagal memuat dashboard."); setData(null); });
  }, [date]);
  useEffect(() => { load(); }, [load]);

  if (err) return <div className="border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] text-rose-700" data-testid="att-dash-err">{err}</div>;
  if (!data) return <div className="p-4 text-xs text-slate-500">Memuat dashboard…</div>;

  return (
    <div className="space-y-3" data-testid="att-dash">
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="font-display text-[13px] uppercase tracking-wider text-[#0B1D3A] flex items-center gap-2">
          <BarChart3 size={14} /> Dashboard Kehadiran
        </h3>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-7 w-36 text-[11px]" data-testid="att-dash-date" />
        <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]" onClick={load} data-testid="att-dash-reload"><RefreshCw size={10} /> Muat Ulang</Button>
        <span className="text-[10px] text-slate-500 italic ml-auto">{data.scope} · snapshot {new Date(data.snapshot_at).toLocaleTimeString("id-ID")}</span>
      </div>
      <div className="border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900 flex items-start gap-2">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        <span>{data.note}</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {Object.keys(BUCKET_META).map((k) => {
          const meta = BUCKET_META[k];
          const Icon = meta.icon;
          const n = data.counters[k] || 0;
          const active = open === k;
          return (
            <button key={k} type="button" onClick={() => setOpen(active ? null : k)} data-testid={`att-dash-counter-${k}`}
              className={`border px-3 py-2 text-left transition ${active ? meta.activeCls : "border-slate-200 bg-white hover:bg-slate-50"}`}>
              <div className="flex items-center justify-between">
                <Icon size={14} className={meta.iconCls} />
                <span className="text-[9px] uppercase tracking-wider text-slate-400">{active ? "▲" : "▼"}</span>
              </div>
              <div className="font-display text-2xl tabular-nums text-[#0B1D3A] mt-1">{n}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{meta.label}</div>
            </button>
          );
        })}
      </div>
      {open && (
        <div className="border border-slate-200 bg-white" data-testid={`att-dash-detail-${open}`}>
          <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <span className="font-display text-[11px] uppercase tracking-wider text-[#0B1D3A]">{BUCKET_META[open].label} — {data.counters[open] || 0} orang</span>
            <button type="button" onClick={() => setOpen(null)} className="text-[10px] text-slate-500 hover:text-slate-800" data-testid="att-dash-detail-close">Tutup</button>
          </div>
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[9px] uppercase tracking-wider text-slate-600"><tr>
                <th className="text-left px-3 py-1.5">Karyawan</th>
                <th className="text-left px-3 py-1.5">Lokasi</th>
                <th className="text-left px-3 py-1.5">Masuk</th>
                <th className="text-left px-3 py-1.5">Pulang</th>
                <th className="text-right px-3 py-1.5">Catatan</th>
              </tr></thead>
              <tbody>
                {(data.buckets[open] || []).length === 0 && <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-400 italic">Belum ada data pada bucket ini.</td></tr>}
                {(data.buckets[open] || []).map((u, i) => (
                  <tr key={`${u.user_id}-${i}`} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`att-dash-row-${open}-${u.user_id}`}>
                    <td className="px-3 py-1.5 font-semibold">
                      <button type="button" onClick={() => onDrill?.(u.user_id)} className="hover:text-[#FF5A1F]" data-testid={`att-dash-drill-${u.user_id}`}>{u.user_name || u.user_id}</button>
                    </td>
                    <td className="px-3 py-1.5 text-[10px]">{u.location_name || "—"}</td>
                    <td className="px-3 py-1.5 tabular-nums">{fmtTime(u.clock_in_at, u.timezone)}</td>
                    <td className="px-3 py-1.5 tabular-nums">{fmtTime(u.clock_out_at, u.timezone)}</td>
                    <td className="px-3 py-1.5 text-right text-[10px]">
                      {u.late_minutes > 0 && <span className="text-rose-700">Terlambat {u.late_minutes}m</span>}
                      {u.kind && <span className="text-amber-700 ml-1">{u.kind}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ================= Tabel Rekap ================= */
function RekapTable({ filters, setFilters, users, locations }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(() => {
    setLoading(true); setErr(null);
    const q = new URLSearchParams({ date_from: filters.date_from, date_to: filters.date_to });
    ["user_id", "location_id", "status_filter", "validation_filter", "source_filter", "break_kind_filter", "overtime_filter"].forEach((k) => {
      if (filters[k]) q.append(k, filters[k]);
    });
    api.get(`/attendance/rekap?${q}`).then((r) => setData(r.data))
      .catch((e) => { setErr(formatDetail(e.response?.data?.detail) || "Gagal memuat rekap."); setData(null); })
      .finally(() => setLoading(false));
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const doExport = () => {
    if (exporting) return;
    const q = new URLSearchParams({ date_from: filters.date_from, date_to: filters.date_to });
    ["user_id", "location_id", "status_filter", "validation_filter", "source_filter", "break_kind_filter", "overtime_filter"].forEach((k) => {
      if (filters[k]) q.append(k, filters[k]);
    });
    setExporting(true);
    api.get(`/attendance/rekap/export.xlsx?${q}`, { responseType: "blob" })
      .then((r) => {
        const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `rekap_kehadiran_${filters.date_from}_${filters.date_to}.xlsx`;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success("Ekspor berhasil — rekap mengikuti filter aktif.");
      })
      .catch((e) => toast.error(formatDetail(e.response?.data?.detail) || "Gagal mengekspor."))
      .finally(() => setExporting(false));
  };

  return (
    <div className="space-y-3" data-testid="att-rekap-table">
      <div className="border border-slate-200 bg-white p-3 space-y-2" data-testid="att-rekap-filters">
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <Filter size={12} className="text-[#0B1D3A]" />
          <span className="font-display text-[11px] uppercase tracking-wider text-[#0B1D3A]">Filter</span>
          <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="h-7 w-36" data-testid="att-rekap-from" />
          <span className="text-slate-400">s/d</span>
          <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="h-7 w-36" data-testid="att-rekap-to" />
          <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]" onClick={load} disabled={loading} data-testid="att-rekap-reload">
            <RefreshCw size={10} className={loading ? "animate-spin" : ""} /> Terapkan
          </Button>
          <Button size="sm" className="rounded-none h-7 text-[10px] bg-[#0B1D3A] hover:bg-[#16305c] ml-auto" onClick={doExport} disabled={exporting} data-testid="att-rekap-export"><Download size={10} /> {exporting ? "Mengekspor…" : "Export Excel"}</Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <SearchableSelect value={filters.user_id || ""} onChange={(v) => setFilters({ ...filters, user_id: v })}
            options={[{ value: "", label: "Semua karyawan" }, ...users.map((u) => ({ value: u.id, label: `${u.name} — ${u.division || "—"}` }))]}
            placeholder="Karyawan" testid="att-rekap-user" />
          <SearchableSelect value={filters.location_id || ""} onChange={(v) => setFilters({ ...filters, location_id: v })}
            options={[{ value: "", label: "Semua lokasi" }, ...locations.map((l) => ({ value: l.id, label: l.name }))]}
            placeholder="Lokasi" testid="att-rekap-loc" />
          <SearchableSelect value={filters.status_filter || ""} onChange={(v) => setFilters({ ...filters, status_filter: v })}
            options={[{ value: "", label: "Semua status" }, ...Object.keys(STATUS_META).map((k) => ({ value: k, label: STATUS_META[k].label }))]}
            placeholder="Status" testid="att-rekap-status" />
          <SearchableSelect value={filters.validation_filter || ""} onChange={(v) => setFilters({ ...filters, validation_filter: v })}
            options={[{ value: "", label: "Semua validasi" }, { value: "valid", label: "Valid" }, { value: "needs_review", label: "Perlu peninjauan" }]}
            placeholder="Validasi" testid="att-rekap-validation" />
          <SearchableSelect value={filters.source_filter || ""} onChange={(v) => setFilters({ ...filters, source_filter: v })}
            options={[{ value: "", label: "Semua sumber" }, { value: "manual_web", label: "Web manual" }, { value: "machine_import", label: "Impor mesin" }, { value: "correction", label: "Hasil koreksi" }]}
            placeholder="Sumber" testid="att-rekap-source" />
          <SearchableSelect value={filters.break_kind_filter || ""} onChange={(v) => setFilters({ ...filters, break_kind_filter: v })}
            options={[{ value: "", label: "Semua keluar" }, { value: "office_duty", label: "Tugas luar" }, { value: "personal", label: "Izin pribadi" }]}
            placeholder="Jenis keluar" testid="att-rekap-break" />
          <SearchableSelect value={filters.overtime_filter || ""} onChange={(v) => setFilters({ ...filters, overtime_filter: v })}
            options={[{ value: "", label: "Semua lembur" }, { value: "has_overtime", label: "Ada lembur aktual" }, { value: "approved_overtime", label: "Lembur disetujui" }]}
            placeholder="Lembur" testid="att-rekap-ot" />
        </div>
      </div>
      {err && <div className="border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] text-rose-700" data-testid="att-rekap-err">{err}</div>}
      <div className="text-[10px] text-slate-500" data-testid="att-rekap-count">{data ? `${data.count} baris · ${data.scope}` : "Memuat…"}</div>
      <div className="border border-slate-200 overflow-x-auto max-h-[520px] overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[9px] uppercase tracking-wider text-slate-600 sticky top-0 z-10"><tr>
            <th className="text-left px-2 py-2">Tanggal</th>
            <th className="text-left px-2 py-2">Karyawan</th>
            <th className="text-left px-2 py-2">Lokasi</th>
            <th className="text-left px-2 py-2">Jadwal</th>
            <th className="text-right px-2 py-2">Masuk / Pulang</th>
            <th className="text-right px-2 py-2">Rentang</th>
            <th className="text-right px-2 py-2">Di Lokasi</th>
            <th className="text-right px-2 py-2">Tugas Luar</th>
            <th className="text-right px-2 py-2">Pribadi</th>
            <th className="text-right px-2 py-2">Istirahat</th>
            <th className="text-right px-2 py-2">Terlambat</th>
            <th className="text-right px-2 py-2">Lembur Aktual / Disetujui</th>
            <th className="text-left px-2 py-2">Sumber</th>
            <th className="text-left px-2 py-2">Validasi</th>
            <th className="text-left px-2 py-2">Catatan</th>
          </tr></thead>
          <tbody>
            {!data?.rows?.length && <tr><td colSpan={15} className="px-3 py-8 text-center text-slate-400 italic">Belum ada data pada rentang & filter ini.</td></tr>}
            {data?.rows?.map((r, i) => (
              <tr key={`${r.user_id}-${r.work_date}-${i}`} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`att-rekap-row-${r.user_id}-${r.work_date}`}>
                <td className="px-2 py-1.5 tabular-nums whitespace-nowrap">{r.work_date}</td>
                <td className="px-2 py-1.5 font-semibold">{r.user_name}<div className="text-[9px] text-slate-400">{r.division}</div></td>
                <td className="px-2 py-1.5 text-[10px]">{(r.location || {}).name || "—"}</td>
                <td className="px-2 py-1.5 text-[10px]">{(r.shift || {}).start_time || "—"}–{(r.shift || {}).end_time || "—"}</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-[10px]">{fmtTime(r.clock_in_at, r.timezone)} / {fmtTime(r.clock_out_at, r.timezone)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{fmtHM(r.rentang_minutes)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{fmtHM(r.at_location_minutes)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-sky-700">{fmtHM(r.office_duty_minutes)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-amber-700">{fmtHM(r.personal_minutes)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-[10px]">{fmtHM((r.shift || {}).break_minutes)}</td>
                <td className={`px-2 py-1.5 text-right tabular-nums ${r.late_minutes > 0 ? "text-rose-700 font-semibold" : "text-slate-400"}`}>{fmtHM(r.late_minutes)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-[10px]">{fmtHM(r.overtime_actual_net_minutes)} / <span className="font-semibold">{fmtHM(r.overtime_approved_minutes)}</span></td>
                <td className="px-2 py-1.5 text-[9px] uppercase tracking-wider text-slate-500">{(r.sources || []).join(" + ") || "—"}</td>
                <td className="px-2 py-1.5"><span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 border ${r.needs_review ? "text-rose-700 bg-rose-50 border-rose-200" : "text-emerald-700 bg-emerald-50 border-emerald-200"}`}>{r.needs_review ? "Perlu peninjauan" : "Valid"}</span></td>
                <td className="px-2 py-1.5 text-[10px]">
                  {r.was_corrected && <span className="text-amber-700">koreksi disetujui · </span>}
                  {r.pending_corrections?.length > 0 && <span className="text-amber-700">{r.pending_corrections.length} pengajuan menunggu · </span>}
                  {(r.leave || {}).leave_type && <span className="text-indigo-700">cuti: {r.leave.leave_type}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ================= Panel utama ================= */
export default function RekapKehadiranPanel({ meta }) {
  const canView = !!meta?.can_approve || !!meta?.can_configure;  // atasan/HR/BOD
  const [tab, setTab] = useState("dashboard");
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [filters, setFilters] = useState({
    date_from: monthStartStr(), date_to: todayStr(),
    user_id: "", location_id: "", status_filter: "",
    validation_filter: "", source_filter: "", break_kind_filter: "", overtime_filter: "",
  });

  useEffect(() => {
    if (!canView) return;
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
    api.get("/attendance/locations").then((r) => setLocations(r.data || [])).catch(() => setLocations([]));
  }, [canView]);

  if (!canView) {
    return (
      <div className="border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600" data-testid="att-rekap-readonly">
        Rekap Kehadiran hanya untuk HR, atasan (pemeriksa penugasan), dan BOD. Permission <code className="bg-white px-1">attendance.approve/update</code> diperlukan.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="att-rekap-panel">
      <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto">
        {[["dashboard", "Dashboard"], ["tabel", "Tabel Rekap & Ekspor"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`att-rekap-sub-${k}`}
            className={`px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold border-b-2 whitespace-nowrap ${tab === k ? "border-[#FF5A1F] text-[#0B1D3A]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>{l}</button>
        ))}
      </div>
      {tab === "dashboard" && <DashboardSection onDrill={(uid) => { setFilters((f) => ({ ...f, user_id: uid })); setTab("tabel"); }} />}
      {tab === "tabel" && <RekapTable filters={filters} setFilters={setFilters} users={users} locations={locations} />}
    </div>
  );
}
